import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";

const ProjectGroupDetails = ({
  isLoading,
  isError,
  countriesLoading,
  countriesError,
  countries,
  tenants,
  assetTypesLoading,
  assetTypesError,
  assetTypes,
  onFormChange,
  formData,
  formErrors,
  existingPublicIds = []
}) => {
  const [filteredAssetTypes, setFilteredAssetTypes] = useState([]);
  const [segments, setSegments] = useState([]);
  const [isDuplicatePublicId, setIsDuplicatePublicId] = useState(false);

  // ensure unique segments from asset types
  useEffect(() => {
    if (assetTypes && assetTypes.length > 0) {
      const uniqueSegments = [...new Set(assetTypes.map((type) => type.segment))];
      setSegments(uniqueSegments);
    }
  }, [assetTypes]);

  useEffect(() => {
    if (formData.segment && assetTypes) {
      const filtered = assetTypes.filter((type) => type.segment === formData.segment);
      setFilteredAssetTypes(filtered);
    } else {
      setFilteredAssetTypes([]);
    }
  }, [formData.segment, assetTypes]);

  // check for duplicate publicId when the field changes or when existingPublicIds updates
  useEffect(() => {
    if (formData.publicId && existingPublicIds.length > 0) {
      const publicIdValue = parseInt(formData.publicId, 10);
      setIsDuplicatePublicId(existingPublicIds.includes(publicIdValue));
    } else {
      setIsDuplicatePublicId(false);
    }
  }, [formData.publicId, existingPublicIds]);

  const handleFieldChange = (e) => {
    const { id, value } = e.target;
    onFormChange(id, value);
  };

  const handleSelectChange = (id, selectedOption) => {
    onFormChange(id, selectedOption?.value || "");
  };

  const handleTenantChange = (selectedOption) => {
    onFormChange("tenantId", selectedOption?.value || "");
    onFormChange("tenantName", selectedOption?.label || "");
  };

  const handlePublicIdChange = (e) => {
    const { id, value } = e.target;
    onFormChange(id, value);

    // real-time validation for publicId
    if (value) {
      const publicIdValue = parseInt(value, 10);
      const isDuplicate = existingPublicIds.includes(publicIdValue);
      if (isDuplicate) {
        onFormChange("_publicIdError", "This Public ID already exists");
      } else {
        onFormChange("_publicIdError", null);
      }
    }
  };

  const countryOptions =
    countries?.map((country) => ({
      value: country,
      label: country
    })) || [];

  const assetTypeOptions =
    filteredAssetTypes.map((type) => ({
      value: type.id,
      label: type.name
    })) || [];

  const segmentOptions =
    segments.map((segment) => ({
      value: segment,
      label: segment
    })) || [];

  // Ensure tenantOptions is properly created with null check
  const tenantOptions =
    tenants && tenants.length > 0
      ? tenants.map((tenant) => ({
          value: tenant.id,
          label: tenant.name
        }))
      : [];

  return (
    <div className="project-group-section mb-5 p-4 bg-light border rounded">
      <h5 className="text-white p-2" style={{ backgroundColor: "#243544" }}>
        Project Group Details
      </h5>
      <Row>
        <Col md={6}>
          <Form.Group controlId="name" className="mb-4">
            <Form.Label>
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Project Group Name"
              value={formData.name}
              onChange={handleFieldChange}
              required
              isInvalid={!!formErrors?.name}
            />
            {formErrors?.name && <Form.Control.Feedback type="invalid">{formErrors.name}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="operator" className="mb-4">
            <Form.Label>Operator</Form.Label>
            <Form.Control type="text" placeholder="Enter Operator" value={formData.operator} onChange={handleFieldChange} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group controlId="segment" className="mb-4">
            <Form.Label>
              Segment <span className="text-danger">*</span>
            </Form.Label>
            {assetTypesLoading && <p>Loading segments...</p>}
            {assetTypesError && <p className="text-danger">Failed to load segments.</p>}
            {!assetTypesLoading && !assetTypesError && (
              <div>
                <Select
                  options={segmentOptions}
                  onChange={(option) => {
                    handleSelectChange("segment", option);
                    // clear asset type when segment changes
                    onFormChange("groupAssetTypeId", "");
                  }}
                  value={segmentOptions.find((option) => option.value === formData.segment) || null}
                  placeholder="Select Segment"
                  isClearable
                  isSearchable
                  className={formErrors?.segment ? "is-invalid" : ""}
                />
                {formErrors?.segment && <div className="invalid-feedback d-block">{formErrors.segment}</div>}
              </div>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="groupAssetTypeId" className="mb-4">
            <Form.Label>
              Asset Type <span className="text-danger">*</span>
            </Form.Label>
            {assetTypesLoading && <p>Loading asset types...</p>}
            {assetTypesError && <p className="text-danger">Failed to load asset types.</p>}
            {!assetTypesLoading && !assetTypesError && (
              <div>
                <Select
                  options={assetTypeOptions}
                  onChange={(option) => handleSelectChange("groupAssetTypeId", option)}
                  value={assetTypeOptions.find((option) => option.value === formData.groupAssetTypeId) || null}
                  placeholder={formData.segment ? "Select Asset Type" : "Select a Segment first"}
                  isDisabled={!formData.segment}
                  isClearable
                  isSearchable
                  className={formErrors?.groupAssetTypeId ? "is-invalid" : ""}
                />
                {formErrors?.groupAssetTypeId && <div className="invalid-feedback d-block">{formErrors.groupAssetTypeId}</div>}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group controlId="country" className="mb-4">
            <Form.Label>
              Country <span className="text-danger">*</span>
            </Form.Label>
            {countriesLoading && <p>Loading countries...</p>}
            {countriesError && <p className="text-danger">Failed to load countries.</p>}
            {!countriesLoading && !countriesError && (
              <div>
                <Select
                  options={countryOptions}
                  onChange={(option) => handleSelectChange("country", option)}
                  value={countryOptions?.find((option) => option.value === formData.country) || null}
                  placeholder="Select Country"
                  isClearable
                  isSearchable
                  className={formErrors?.country ? "is-invalid" : ""}
                />
                {formErrors?.country && <div className="invalid-feedback d-block">{formErrors.country}</div>}
              </div>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="region" className="mb-4">
            <Form.Label>Region</Form.Label>
            <Form.Control type="text" placeholder="Enter Region" value={formData.region} onChange={handleFieldChange} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group controlId="publicId" className="mb-4">
            <Form.Label>Public ID</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter Public ID"
              value={formData.publicId}
              onChange={handlePublicIdChange}
              isInvalid={!!formErrors?.publicId || isDuplicatePublicId}
            />
            {(formErrors?.publicId || isDuplicatePublicId) && (
              <Form.Control.Feedback type="invalid">
                {formErrors?.publicId || "This Public ID already exists for this tenant."}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="tenantId" className="mb-4">
            <Form.Label>
              Tenant <span className="text-danger">*</span>
            </Form.Label>
            {isLoading && <p>Loading tenants...</p>}
            {isError && <p className="text-danger">Failed to load tenants.</p>}
            {!isLoading && !isError && (
              <div>
                <Select
                  options={tenantOptions}
                  onChange={handleTenantChange}
                  value={tenantOptions?.find((option) => option.value === formData.tenantId) || null}
                  placeholder="Search and select a tenant (required)"
                  isClearable
                  isSearchable
                  className={formErrors?.tenantId ? "is-invalid" : ""}
                />
                {formErrors?.tenantId && <div className="invalid-feedback d-block">{formErrors.tenantId}</div>}
              </div>
            )}
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group controlId="latitude" className="mb-4">
            <Form.Label>Latitude</Form.Label>
            <Form.Control type="text" placeholder="Enter Latitude" value={formData.latitude} onChange={handleFieldChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="longitude" className="mb-4">
            <Form.Label>Longitude</Form.Label>
            <Form.Control type="text" placeholder="Enter Longitude" value={formData.longitude} onChange={handleFieldChange} />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default ProjectGroupDetails;
